import { useLingui } from '@lingui/react/macro';
import { Check, Copy } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { useEffect, useState } from 'react';

import type { Notifier } from '@/shared/data';
import { formatPhoneBr } from '@/shared/format/phone';
import { Alert } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { Spinner } from '@/shared/ui/spinner';

// A fase "Autorização" da consulta de margem (DataPrevAuthorizationContainer.tsx), a mesma no Crédito do Trabalhador e
// no INSS: o SMS enviado com o link e a espera, ou o QR Code da conversa por WhatsApp.

export interface MarginAuthorization {
  readonly channel: 'WhatsappInbound' | 'SMS' | 'WebLink';
  readonly link: string;
  readonly smsLink: string;
  readonly phone: string | null;
}

/** O "Copiar link" do WhatsApp (DataPrevAuthorizationContainer.tsx), com o aviso do legado. */
export function copyAuthorizationLink(notifier: Notifier, link: string, t: ReturnType<typeof useLingui>['t']) {
  void navigator.clipboard.writeText(link).then(() => notifier.notify({ tone: 'success', title: t`Link copiado!`, description: t`Link foi copiado para a sua área de transferência` }));
}

function CopyLinkButton({ value }: { readonly value: string }) {
  const { t } = useLingui();
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return undefined;
    const timer = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(timer);
  }, [copied]);
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="size-7 shrink-0 text-muted-foreground"
      aria-label={copied ? t`Copiado` : t`Copiar link de autorização`}
      onClick={() => void navigator.clipboard.writeText(value).then(() => setCopied(true))}
    >
      {copied ? <Check aria-hidden className="size-3.5" /> : <Copy aria-hidden className="size-3.5" />}
    </Button>
  );
}

/**
 * O corpo da autorização: no SMS, o texto do produto, o celular, o link e a espera; no WhatsApp, o QR Code e o aviso
 * (`waiting` = ainda sem autorização).
 */
export function MarginAuthorizationBody({ authorization, smsText, polling, waiting }: { readonly authorization: MarginAuthorization; readonly smsText: string; readonly polling: boolean; readonly waiting: boolean }) {
  const { t } = useLingui();
  if (authorization.channel === 'SMS')
    return (
      <>
        <p className="text-base">{smsText}</p>
        <div className="rounded-card border border-border bg-card p-3">
          <p className="mb-1 text-sm font-semibold text-muted-foreground">{t`Celular do titular`}</p>
          <p className="text-lg font-bold">{authorization.phone ? (formatPhoneBr(authorization.phone) ?? authorization.phone) : t`Não informado`}</p>
        </div>
        {authorization.smsLink ? (
          <div className="rounded-card border border-border bg-card p-3">
            <p className="mb-1 text-sm font-semibold text-muted-foreground">{t`Link de autorização`}</p>
            <div className="flex items-center gap-1">
              <CopyLinkButton value={authorization.smsLink} />
              <span className="font-bold break-all">{authorization.smsLink}</span>
            </div>
          </div>
        ) : null}
        {polling ? (
          <div role="status" className="flex items-start gap-3 rounded-card bg-muted p-3">
            <Spinner aria-hidden />
            <div>
              <p className="font-semibold">{t`Aguardando autorização...`}</p>
              <p className="text-sm text-muted-foreground">{t`Após o titular autorizar via SMS, esta tela será atualizada automaticamente.`}</p>
            </div>
          </div>
        ) : null}
      </>
    );
  if (!authorization.link) return <p className="text-err">{t`Não foi possível gerar o link de autorização. Por favor, tente novamente.`}</p>;
  return (
    <>
      <p className="text-base">{t`Escaneie o QR Code abaixo com seu celular ou clique no botão para copiar o link da conversa via WhatsApp.`}</p>
      {waiting ? (
        <div className="flex justify-center">
          <div className="rounded-card bg-white p-2">
            <QRCodeSVG value={authorization.link} size={260} level="H" marginSize={4} title={t`QR Code da conversa via WhatsApp`} />
          </div>
        </div>
      ) : null}
      <Alert tone={waiting ? 'warn' : 'ok'}>{waiting ? t`Aguardando a autorização do tomador. A tela será atualizada automaticamente assim que for autorizada.` : t`Sucesso! Autorização concluída com sucesso.`}</Alert>
    </>
  );
}
